import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Check } from "lucide-react";
import { fmtTime } from "@/lib/api";

export default function ProtocolRunner({ protocol, onFinish }) {
  const hasTimer = protocol.duration_seconds > 0 && !protocol.while_functioning;
  const [remaining, setRemaining] = useState(protocol.duration_seconds || 0);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (running && hasTimer) {
      timer.current = setInterval(() => {
        setRemaining((r) => {
          if (r <= 1) {
            clearInterval(timer.current);
            setRunning(false);
            setDone(true);
            return 0;
          }
          return r - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer.current);
  }, [running, hasTimer]);

  const total = protocol.duration_seconds || 1;
  const pct = hasTimer ? ((total - remaining) / total) * 100 : done ? 100 : 0;

  return (
    <div className="px-6 pt-8 pb-28">
      <div className="text-center mb-6">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">
          {protocol.duration_label}
        </span>
        <h1 className="mt-2 font-serif-display text-3xl">{protocol.title}</h1>
      </div>

      {hasTimer && (
        <div className="flex flex-col items-center mb-8">
          <div className="relative h-44 w-44">
            <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
              <circle cx="50" cy="50" r="45" fill="none" stroke="#EBE9E4" strokeWidth="3" />
              <circle
                cx="50" cy="50" r="45" fill="none" stroke="#C9A227" strokeWidth="3"
                strokeLinecap="round" strokeDasharray={2 * Math.PI * 45}
                strokeDashoffset={(2 * Math.PI * 45) * (1 - pct / 100)}
                style={{ transition: "stroke-dashoffset 1s linear" }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-mono-ss text-4xl text-[#1A1A1A]" data-testid="protocol-timer">
                {fmtTime(remaining)}
              </span>
            </div>
          </div>
          <button
            data-testid="protocol-toggle"
            onClick={() => setRunning((v) => !v)}
            className="mt-6 inline-flex items-center gap-2 bg-[#F5F3ED] border border-[#EBE9E4] rounded-full px-6 py-3 ss-tap ss-focus"
          >
            {running ? <Pause size={18} strokeWidth={1.5} /> : <Play size={18} strokeWidth={1.5} />}
            {running ? "Pause" : remaining === total ? "Start timer" : "Resume"}
          </button>
        </div>
      )}

      <ol className="space-y-3">
        {protocol.instructions.map((step, i) => (
          <li key={i} className="flex gap-3 ss-card p-4">
            <span className="font-mono-ss text-sm text-[#C9A227] mt-0.5">{i + 1}</span>
            <span className="text-[15px] leading-relaxed text-[#1A1A1A]">{step}</span>
          </li>
        ))}
      </ol>

      <p className="mt-6 text-[14px] italic text-[#474747] leading-relaxed">
        {protocol.completion_prompt}
      </p>

      <button
        data-testid="protocol-finish"
        onClick={onFinish}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus hover:-translate-y-0.5"
      >
        <Check size={18} strokeWidth={2} /> I've finished this
      </button>
    </div>
  );
}
